// src/pages/ProfilePage.js
// Updated: 11/10/2025 09:50 PM EST
// --- THIS IS A NEW FILE ---

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, auth } from '../firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { User, Mail, Lock, Save, AlertCircle, CheckCircle } from 'lucide-react';

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

export default function ProfilePage() {
  const { currentUser, userData, isLoading } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    if (userData?.displayName) {
      setDisplayName(userData.displayName);
    }
  }, [userData]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setStatus({ type: 'error', message: 'Display Name cannot be empty.' });
      return;
    }

    setIsUpdating(true);
    setStatus({ type: '', message: '' });

    try {
      // 1. Update Firebase Auth profile
      await updateProfile(currentUser, { displayName: displayName });

      // 2. Update Firestore User Document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { displayName: displayName });

      setStatus({ type: 'success', message: 'Profile updated successfully.' });
    } catch (err) {
      console.error("Error updating profile:", err);
      setStatus({ type: 'error', message: 'Failed to update profile.' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!window.confirm("Send a password reset email to " + currentUser.email + "?")) return;
    
    setIsUpdating(true);
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setStatus({ type: 'success', message: 'Password reset email sent. Check your inbox.' });
    } catch (err) {
      console.error("Error sending reset email:", err);
      setStatus({ type: 'error', message: 'Failed to send reset email: ' + err.message });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">My Profile</h1>

      {/* Info Card */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center mb-6">
          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold mr-4">
            {userData?.displayName?.charAt(0).toUpperCase() || <User />}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{userData?.displayName}</h2>
            <p className="text-gray-500">{userData?.email}</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-1">
              {userData?.role?.toUpperCase()}
            </span>
          </div>
        </div>

        {status.message && (
          <div className={`mb-4 p-4 rounded-md flex items-center ${status.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {status.type === 'error' ? <AlertCircle className="mr-2 h-5 w-5" /> : <CheckCircle className="mr-2 h-5 w-5" />}
            {status.message}
          </div>
        )}

        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">Display Name</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">This name will appear on your votes and scores.</p>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isUpdating || displayName === userData?.displayName}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {/* Security Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Lock className="mr-2 h-5 w-5 text-gray-500" />
          Security
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">Password</p>
            <p className="text-sm text-gray-500">Send a password reset link to your email.</p>
          </div>
          <button
            onClick={handlePasswordReset}
            disabled={isUpdating}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <Mail className="mr-2 h-4 w-4" />
            Reset Password
          </button>
        </div>
      </div>
    </div>
  );
}